/* eslint-disable jsx-a11y/no-autofocus */

import React, { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import AutoCompleteListBox from 'components/forms/combobox/AutoCompleteListBox'
import { LabelComponent } from 'transfix-ui/components/LabelComponent'
import { getShipmentNoteTags } from 'utils/actions/shipmentNoteTags'
import { getUserTags } from 'utils/actions/tags'
import { selectUserTags } from 'utils/reducers/tags'
import { selectShipmentNoteUserTags } from 'utils/selectors/shipmentNoteTags'
import { useDropdownPosition } from 'utils/dropdownPosition'
import { HASHTAGS } from 'constants/tags'
import { isOptionGroup } from '../combobox/utils'
import 'scss/5_components/_dpl-autocomplete.scss'

const moveBetweenOptions = (optionsList, direction, activeElement) => {
  const activeElementIndex = Array.from(optionsList).indexOf(activeElement)
  const optionsListLength = optionsList.length

  if (activeElementIndex !== -1) {
    activeElement.setAttribute('tabindex', '-1')

    let nextElementIndex

    if (direction === 'up') {
      if (activeElementIndex === 0) {
        nextElementIndex = optionsListLength - 1
      } else {
        nextElementIndex = activeElementIndex - 1
      }
    } else if (activeElementIndex === optionsListLength - 1) {
      nextElementIndex = 0
    } else {
      nextElementIndex = activeElementIndex + 1
    }

    optionsList[nextElementIndex].setAttribute('tabindex', '0')
    optionsList[nextElementIndex].focus()
  }
}

const clickInTextBoxRegion = (event, listBox) => {
  const {
    height: autoCompleteHeight,
    left: xMin,
    top: yMin, // using top instead of y because of IE
    width: autoCompleteWidth, // using left instead of x because of IE
  } = listBox.parentElement.getBoundingClientRect()

  const xMax = xMin + autoCompleteWidth
  const yMax = yMin + autoCompleteHeight

  const xWithinRange = xMin <= event.clientX && event.clientX <= xMax
  const yWithinRange = yMin <= event.clientY && event.clientY <= yMax

  return xWithinRange && yWithinRange
}

export const AutoTagging = props => {
  const userInputTags = useSelector(selectUserTags)
  const userShipmentNoteTags = useSelector(selectShipmentNoteUserTags)

  const {
    autoFocus = false,
    className: textareaClass,
    dataTest,
    enterKeyBehavior,
    label: labelProp,
    name,
    onBlur,
    onChange: onChangeBody,
    onEnterKeyPressed,
    onFocus,
    overrideListener = true,
    placeholder,
    rows = 2,
    shipmentId,
    value: body,
  } = props

  const userTags = shipmentId ? userShipmentNoteTags : userInputTags
  const activeTags = userTags
  const tagsList = [...activeTags, ...HASHTAGS]

  const dispatch = useDispatch()
  useEffect(() => {
    if (activeTags && !activeTags.length) {
      if (shipmentId) {
        dispatch(getShipmentNoteTags(shipmentId))
      } else {
        dispatch(getUserTags())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const listBoxRef = useRef(null)
  const triggerRef = useRef(null)

  const [suggestions, setSuggestions] = useState(tagsList)
  const [lastSuggestions, setLastSuggestions] = useState([suggestions])
  const [key, setKey] = useState('')

  const [isOpen, setIsOpen] = useState(false)
  const close = () => setIsOpen(false)
  const [, style] = useDropdownPosition(isOpen, close, triggerRef, true)

  const getHighlightedMarkup = (suggestion, value) => {
    const matches = match(suggestion.text, value.substring(1))
    const parts = parse(suggestion.text, matches)
    return (
      <span>
        {parts.map(part => {
          const className = part.highlight ? 'b' : ''

          return (
            <span className={className} key={part.text}>
              {part.text}
            </span>
          )
        })}
      </span>
    )
  }

  const markup = (suggestion, textBody) => {
    const allEntries = textBody.trim().split(' ')
    const lastEntry = allEntries[allEntries.length - 1]
    if (!lastEntry) return null

    return (
      <div>
        {suggestion.type}
        {getHighlightedMarkup(suggestion, lastEntry)}: {suggestion.description}
      </div>
    )
  }

  const addMarkupToSuggestions = (value, currentSuggestions = []) => {
    // Only show suggestions if there is at least 1 char of tag input at the end of current value.
    if (!value.match(/[@#].{1,}$/g)) return null
    if (!currentSuggestions.length) return null
    // Limit suggestions to 50 for performance
    return currentSuggestions
      .slice(0, 10)
      .map(suggestion => ({ text: suggestion.nickname, ...suggestion }))
      .map(suggestion => {
        if (isOptionGroup(suggestion)) {
          return {
            groupName: suggestion.groupName,
            options: suggestion.options.map(nestedOption => ({
              text: nestedOption.text,
              markup: getHighlightedMarkup(nestedOption, value),
            })),
          }
        }
        return {
          id: suggestion.id,
          text: suggestion.text,
          markup: markup(suggestion, value),
        }
      })
  }

  const handleHashTag = searchTerm => {
    const filteredResults = HASHTAGS.filter(({ label = '' }) => {
      const tagLabel = label.toLowerCase()
      return tagLabel.indexOf(searchTerm.toLowerCase()) !== -1
    })

    setSuggestions(filteredResults)
  }

  const handleUserTag = (searchTerm = '') => {
    if (!userTags.length) return
    const tagsFilter = activeTags.filter(({ firstName = '', nickname = '' }) => {
      const tagLabel = nickname.toLowerCase()
      const tagName = firstName.toLowerCase()
      return (
        tagLabel.indexOf(searchTerm.toLowerCase()) !== -1 ||
        tagName.indexOf(searchTerm.toLowerCase()) !== -1
      )
    })
    if (tagsFilter.length) setSuggestions(tagsFilter)
  }

  const handleBackspace = (suggestionsList, lastWord) => {
    const deletedTag = lastWord.indexOf('@') === -1 && lastWord.indexOf('#') === -1
    if (suggestionsList.length && !deletedTag) setSuggestions(suggestionsList.pop())
    else {
      setSuggestions(tagsList)
      setIsOpen(false)
    }
  }

  const tagging = (message = '') => {
    // Fallback because if you use alt + 64, the key is 4, and don't show the suggestions
    const messageLastKey = message[message.length - 1]
    if (
      ['#', '@', 'Backspace', ' '].includes(key) ||
      ['#', '@', ' '].includes(messageLastKey) ||
      isOpen
    ) {
      const words = message.split(' ')
      // this is always getting the last tag entered -- only tags entered at the
      // end of the message body will properly populate the potential tags
      for (let i = words.length - 1; i >= 0; i -= 1) {
        if (key === 'Backspace') {
          const backSuggestions = [...lastSuggestions].slice(0, lastSuggestions.length - 1)
          setLastSuggestions([...backSuggestions])
          handleBackspace(lastSuggestions, words[i])
        } else if (words[i].indexOf('@') !== -1) {
          if (message.match(/@ $/g)) setIsOpen(false)
          if (key === '@' || messageLastKey === '@') setIsOpen(true)
          handleUserTag(words[i].slice(1))
          // only add suggestions to history if new suggestions this time
          const newSuggestions = suggestions.length !== lastSuggestions[lastSuggestions.length - 1]
          const backSuggestions = newSuggestions
            ? [...lastSuggestions, suggestions]
            : lastSuggestions
          setLastSuggestions(backSuggestions)
          break
        } else if (words[i].indexOf('#') !== -1) {
          if (message.match(/# $/g)) setIsOpen(false)
          if (key === '#' || messageLastKey === '#') setIsOpen(true)
          handleHashTag(words[i].slice(1))
          break
        }
      }
    }
  }

  const onChange = note => {
    if (!note.target) return
    tagging(note.target.value, note)
    onChangeBody(note.target.value)
  }

  const onOptionSelection = ({ nickname = '' }) => {
    const words = body.split(' ')
    // find the instance of the query string
    for (let i = words.length - 1; i >= 0; i -= 1) {
      if (words[i].indexOf('@') !== -1) {
        words[i] = `@${nickname} `
        break
      } else if (words[i].indexOf('#') !== -1) {
        words[i] = `#${nickname} `
        break
      }
    }
    const updatedBody = words.join(' ')
    onChangeBody(updatedBody)
    setLastSuggestions(suggestions)
    return nickname
  }

  const selectOption = ({ text }) => {
    onOptionSelection(suggestions.find(({ nickname }) => nickname === text) || {})
    setIsOpen(false)
  }

  useLayoutEffect(() => {
    const triggerRefNode = triggerRef.current
    const listBoxRefNode = listBoxRef.current

    const handleTriggerEvents = event => {
      event.stopPropagation()

      const optionsList = listBoxRefNode ? listBoxRefNode.querySelectorAll('[role="option"]') : []
      const optionsListLength = optionsList.length
      switch (event.key) {
        case 'Escape':
          /*
            Clears the textbox
            If the listbox is displayed, closes it.
          */
          onChange({ value: '' })
          setIsOpen(false)
          break
        case 'Up':
        case 'ArrowUp':
          /*
            If the listbox is displayed, moves focus to the last suggested value.
          */
          // prevents scrolling default behavior
          event.preventDefault()
          if (optionsListLength) {
            optionsList[optionsListLength - 1].setAttribute('tabindex', '0')
            optionsList[optionsListLength - 1].focus()
          }
          break
        case 'Down':
        case 'ArrowDown':
          /*
            Moves focus to the first suggested value.
          */
          // prevents scrolling default behavior
          event.preventDefault()
          if (optionsListLength) {
            optionsList[0].setAttribute('tabindex', '0')
            optionsList[0].focus()
          }
          break
        case 'Enter':
          /*
            If the enter key behavior is 'submit' and shift key is not pressed should call 'onEnterKeyPressed'
          */
          if (
            enterKeyBehavior === 'submit' &&
            typeof onEnterKeyPressed === 'function' &&
            !event.shiftKey
          ) {
            onEnterKeyPressed(event)
          }
          break
        default:
          /*
            Keys used for cursor movement and text manipulation, such as Delete and Shift + Right Arrow.
            An HTML input with type="text" is used for the textbox so the browser will provide platform-specific editing keys.
          */
          setKey(event.key)
          break
      }
    }

    const handleListBoxEvents = event => {
      event.preventDefault()
      event.stopPropagation()

      const optionsList = listBoxRefNode ? listBoxRefNode.querySelectorAll('[role="option"]') : []

      switch (event.key) {
        case 'Tab':
        case 'Enter':
          /*
            Sets the textbox value to the content of the focused option in the listbox.
            Closes the listbox.
            Sets focus on the textbox.
          */
          if (document.activeElement) {
            const { activeElement } = document
            const activeElementIndex = Array.from(optionsList).indexOf(activeElement)

            if (activeElementIndex !== -1) {
              const optionText = activeElement.getAttribute('aria-label')
              if (optionText) selectOption({ text: optionText })
            }
          }

          if (triggerRefNode) {
            triggerRefNode.focus()
            setIsOpen(false)
          }
          break
        case 'Escape':
          /*
            Closes the listbox.
            Sets focus on the textbox.
            Clears the textbox.
          */

          if (triggerRefNode) {
            triggerRefNode.focus()
            onChange({ value: '' })
            setIsOpen(false)
          }
          break
        case 'Down':
        case 'ArrowDown':
          /*
            Moves focus to the next option.
            If focus is on the last option, moves focus to the first option.
            Note: This wrapping behavior is useful when Home and End move the editing cursor as described below.
          */
          moveBetweenOptions(optionsList, 'down', document.activeElement)
          break
        case 'Up':
        case 'ArrowUp':
          /*
            Moves focus to the previous option.
            If focus is on the first option, moves focus to the last option.
            Note: This wrapping behavior is useful when Home and End move the editing cursor as described below.
          */
          moveBetweenOptions(optionsList, 'up', document.activeElement)
          break
        default:
          /*
            Moves focus to the textbox.
            Types the character in the textbox.
          */
          if (triggerRefNode) {
            triggerRefNode.focus()
            setIsOpen(false)
          }
          break
      }
    }

    const handleTextBoxClick = event => {
      // when click event doesn't occur within co-ordinates of listBox, close it
      if (listBoxRefNode) {
        const shouldShowListBox = !clickInTextBoxRegion(event, listBoxRefNode)
        if (isOpen) setIsOpen(shouldShowListBox)
      }
    }

    if (triggerRefNode && listBoxRefNode) {
      triggerRefNode.addEventListener('keydown', handleTriggerEvents, true)
      listBoxRefNode.addEventListener('keydown', handleListBoxEvents, true)
      if (!overrideListener) document.addEventListener('click', handleTextBoxClick, true)
    }
    return () => {
      if (triggerRefNode) {
        triggerRefNode.removeEventListener('keydown', handleTriggerEvents, true)
      }
      if (listBoxRefNode) {
        listBoxRefNode.removeEventListener('keydown', handleListBoxEvents, true)
      }
      if (!overrideListener) document.removeEventListener('click', handleTextBoxClick, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerRef.current, listBoxRef.current, suggestions])

  const options = addMarkupToSuggestions(body, suggestions) || []

  return (
    <div
      role='combobox'
      className='dpl-autocomplete'
      aria-haspopup='listbox'
      aria-owns='listBoxID'
      aria-controls='listBoxID'
      aria-expanded={isOpen && options && options.length > 0}>
      {labelProp && <LabelComponent label={labelProp} />}
      <textarea
        autoFocus={autoFocus}
        placeholder={placeholder}
        name={name}
        data-test={dataTest || 'note-body'}
        value={body}
        onChange={onChange}
        ref={triggerRef}
        className={textareaClass}
        rows={rows}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <AutoCompleteListBox
        isOpen={isOpen}
        onOptionSelection={selectOption}
        isLoading={false}
        listBoxID='listBoxID'
        ref={listBoxRef}
        options={{ options }}
        style={style}
      />
    </div>
  )
}
